import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { AppShell } from "../components/AppShell";
import { InviteCanvas } from "../components/InviteCanvas";
import { ImageUploadField } from "../components/ImageUploadField";
import { DisplayModePicker } from "../components/DisplayModePicker";
import { MessageField, MessagePlacementPicker } from "../components/MessageField";
import { StyleRow } from "../components/StyleRow";
import { TEXT_KINDS } from "../lib/inviteLayout";
import { GuestVisibilityPicker } from "../components/GuestVisibilityPicker";
import { RsvpFieldsPicker } from "../components/RsvpFieldsPicker";
import { InvitePasswordField } from "../components/InvitePasswordField";
import { PageLoader } from "../context/AuthContext";
import { api, formatApiError, imageUrl } from "../lib/api";

const EMPTY = { occasion: "", sub_header: "", date: "", time: "", place: "", display_mode: "overlay", max_additional_guests: 0, rsvp_x: 50, rsvp_y: 88, image_path: null, notify_on_rsvp: true, guest_list_visibility: "hidden", email_field: "required", phone_field: "off", note_field: "optional", comment_field: "off", password_enabled: false, invite_password: "", title_color: null, subheader_color: null, details_color: null, message: "", message_color: null, message_placement: "auto", ...Object.fromEntries(TEXT_KINDS.flatMap((k) => [[`${k}_pos`, null], [`${k}_size`, null]])) };

const Section = ({ step, title, children }) => (
  <section className="rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-8">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700">{step}</p>
    <h2 className="font-display mb-6 mt-1 text-lg font-bold text-slate-900">{title}</h2>
    <div className="space-y-5">{children}</div>
  </section>
);

const Field = ({ label, children, error }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</Label>
    {children}
    {error && <p data-testid="event-occasion-error" className="text-xs font-semibold text-red-600">{error}</p>}
  </div>
);

const SubLabel = ({ children }) => <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{children}</p>;

export default function EventEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [accessToken, setAccessToken] = useState(null);
  const [isCohost, setIsCohost] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const input = (k) => ({ value: form[k], onChange: (e) => set(k)(e.target.value), className: "h-12 rounded-xl" });
  const moveText = useCallback(
    (updates) => setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(updates).map(([k, v]) => [`${k}_pos`, v])) })),
    []
  );
  const hasPositions = TEXT_KINDS.some((k) => form[`${k}_pos`]);
  const resetLayout = () => setForm((f) => ({ ...f, ...Object.fromEntries(TEXT_KINDS.map((k) => [`${k}_pos`, null])) }));

  useEffect(() => {
    if (!id) return;
    api
      .get(`/events/${id}`)
      .then(({ data }) => {
        setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data[k] ?? EMPTY[k]])));
        setAccessToken(data.access_token);
        setIsCohost(data.role === "cohost");
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        navigate("/dashboard");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.occasion.trim()) return setError("Occasion is required");
    if (form.password_enabled && form.invite_password.trim().length < 4) return toast.error("Invite password must be at least 4 characters");
    setError("");
    setSaving(true);
    const payload = { ...form, date: form.date || null, time: form.time || null, max_additional_guests: Math.max(0, parseInt(form.max_additional_guests, 10) || 0) };
    try {
      const { data } = id ? await api.put(`/events/${id}`, payload) : await api.post("/events", payload);
      toast.success(id ? "Invite updated" : "Invite created. Share the link!");
      navigate(`/events/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AppShell><PageLoader /></AppShell>;

  return (
    <AppShell>
      <Link to="/dashboard" data-testid="editor-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> All parties
      </Link>
      <h1 className="font-display mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">{id ? "Edit invite" : "New invite"}</h1>
      <form onSubmit={save} noValidate className="mt-10 grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)]" data-testid="event-editor-form">
        <div className="space-y-6">
          <Section step="Step 1" title="The image">
            <ImageUploadField value={form.image_path} onChange={set("image_path")} token={accessToken} />
          </Section>
          <Section step="Step 2" title="The details">
            <Field label="Occasion *" error={error}>
              <Input data-testid="event-occasion-input" placeholder="Mia's 30th Birthday" {...input("occasion")} />
              <StyleRow kind="title" form={form} set={set} />
            </Field>
            <Field label="Sub-header">
              <Input data-testid="event-subheader-input" placeholder="Dinner, drinks & dancing" {...input("sub_header")} />
              <StyleRow kind="subheader" form={form} set={set} />
            </Field>
            <div className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Date"><Input type="date" data-testid="event-date-input" {...input("date")} /></Field>
                <Field label="Time"><Input type="time" data-testid="event-time-input" {...input("time")} /></Field>
              </div>
              <Field label="Place">
                <Input data-testid="event-place-input" placeholder="The Rooftop, 12 Main St" {...input("place")} />
                <p className="pt-1 text-[11px] text-slate-400">Color and size for date, time &amp; place:</p>
                <StyleRow kind="details" form={form} set={set} />
              </Field>
            </div>
            <Field label="Message (optional)">
              <MessageField form={form} set={set} />
              <StyleRow kind="message" form={form} set={set} />
            </Field>
            <p className="text-xs text-slate-500">Sizes are a share of the image width, so they look the same on phones, desktops and in the email GIF.</p>
          </Section>
          <Section step="Step 3" title="How should the details appear?">
            <DisplayModePicker value={form.display_mode} onChange={set("display_mode")} />
            {form.display_mode === "none" && <p className="text-xs text-slate-500">Details are hidden in this display mode; only the message (if any) is shown.</p>}
            <div>
              <SubLabel>Message placement</SubLabel>
              <MessagePlacementPicker form={form} set={set} />
            </div>
            <div data-testid="drag-hint" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-3">
              <p className="text-xs text-slate-600">Drag the RSVP button and the text in the live preview to position them.</p>
              {hasPositions && (
                <button type="button" data-testid="reset-text-layout-btn" onClick={resetLayout} className="text-xs font-semibold text-blue-700 hover:text-blue-800">
                  Reset text layout
                </button>
              )}
            </div>
          </Section>
          <Section step="Step 4" title="Guests">
            <Field label="Show guest list to invitees">
              <GuestVisibilityPicker value={form.guest_list_visibility} onChange={set("guest_list_visibility")} />
            </Field>
            <Field label="Max additional guests per invitee">
              <Input type="number" min={0} max={50} data-testid="event-max-guests-input" {...input("max_additional_guests")} className="h-12 w-32 rounded-xl" />
            </Field>
            <label htmlFor="notify-toggle" className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <span>
                <span className="block text-sm font-semibold text-slate-900">{isCohost ? "Email the event owner when guests RSVP" : "Email me when guests RSVP"}</span>
                <span className="block text-xs text-slate-500">{isCohost ? "Alerts go to the owner only, not to co-hosts." : "Get an alert for every new or changed response."}</span>
              </span>
              <Switch id="notify-toggle" data-testid="event-notify-toggle" checked={form.notify_on_rsvp} onCheckedChange={set("notify_on_rsvp")} />
            </label>
          </Section>
          <Section step="Step 5" title="RSVP form fields">
            <RsvpFieldsPicker form={form} onChange={(k, v) => set(k)(v)} />
          </Section>
          <Section step="Step 6" title="Privacy">
            <InvitePasswordField form={form} set={set} />
          </Section>
          <Button type="submit" disabled={saving} data-testid="save-event-button" className="h-14 w-full rounded-lg text-base font-semibold sm:w-auto sm:px-12">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : id ? "Save changes" : "Create invite"}
          </Button>
        </div>
        <aside className="lg:sticky lg:top-24">
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700">Live guest preview</p>
            <p className="text-xs text-slate-500">Drag the RSVP button and text</p>
          </div>
          <InviteCanvas editable event={form} imageSrc={imageUrl(form.image_path, accessToken)} onMove={(x, y) => setForm((f) => ({ ...f, rsvp_x: x, rsvp_y: y }))} onTextMove={moveText} />
        </aside>
      </form>
    </AppShell>
  );
}
